import { useCallback, useEffect, useMemo, useRef } from "react";

import fastDeepEqual from "fast-deep-equal";
import { isObject, transform } from "lodash";

import { useUpdateAgentVersion } from "src/agents/queries/management";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { toastActions } from "src/design-system/Toast/utils";
import { debounceAndSaveArgs } from "src/utils/debounceAndSaveArgs";
import { logger } from "src/utils/logger";

/**
 * Wraps useUpdateAgentVersion with debounce + sequential queue logic.
 * - Uses the existing mutation
 * - Adds debounce to prevent excessive API calls
 * - Adds queue behavior via pendingConfigRef
 * - Tracks etag in ref to avoid stale closures
 */
type UseSequentialAutoSaveOptions = {
  agentId: string;
  versionId: string;
  name: string;
  description: string;
  initialEtag: string;
  initialConfiguration: AgentVersionConfigurationInput;
  disabled?: boolean;
  onSave?: () => void;
};

// When checking if the configuration has changed, we need to prune undefined values to avoid false positives.
const prune = (obj: Record<string, unknown> | object) => {
  return transform(obj, (result, value, key) => {
    // Skip undefined values
    if (value === undefined) return;

    // Recurse if it's an object or array
    (result as Record<string, unknown>)[key] = isObject(value)
      ? prune(value)
      : value;
  });
};

export const useSequentialAutoSave = ({
  agentId,
  versionId,
  name,
  description,
  initialEtag,
  initialConfiguration,
  disabled,
  onSave,
}: UseSequentialAutoSaveOptions) => {
  // Use the existing mutation - it already handles cache updates, optimistic updates, rollback
  const updateMutation = useUpdateAgentVersion();

  // Stores latest data waiting to be sent if there's already an ongoing save call
  const pendingConfigRef = useRef<AgentVersionConfigurationInput | null>(null);

  // Track the last successfully saved configuration to avoid redundant saves
  // Initialize with the initial configuration to prevent saving on first mount
  const lastSavedConfigRef =
    useRef<AgentVersionConfigurationInput>(initialConfiguration);

  // Track latest etag - updated after each successful save
  const etagRef = useRef(initialEtag);

  // Track current versionId to detect stale mutations
  const versionIdRef = useRef(versionId);

  // Keep etag in sync when prop changes (from query cache updates)
  useEffect(() => {
    etagRef.current = initialEtag;
  }, [initialEtag]);

  // Reset refs when version changes to avoid stale data
  useEffect(() => {
    versionIdRef.current = versionId;
    lastSavedConfigRef.current = initialConfiguration;
    pendingConfigRef.current = null;
    // This dependency array is intentionally not exhaustive to avoid
    // unnecessary runs if the initialConfiguration "identity" changes
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  const saveWithQueue = useCallback(
    (configuration: AgentVersionConfigurationInput) => {
      // Capture versionId at call time to detect stale mutations
      const mutationVersionId = versionId;

      updateMutation.mutate(
        {
          agentId,
          versionId,
          name,
          description,
          configuration,
          etag: etagRef.current,
        },
        {
          onSuccess: (result) => {
            // Ignore if version changed while mutation was in-flight
            if (mutationVersionId !== versionIdRef.current) {
              return;
            }

            // Update etag ref with new value from server
            etagRef.current = result.etag;

            // Track what we saved to avoid redundant saves
            // Must deep clone because react-hook-form mutates the same object reference
            lastSavedConfigRef.current = structuredClone(configuration);

            // Notify caller of successful save (e.g., for pre-warming playground)
            onSave?.();
          },
          onSettled: (_data, error) => {
            // Ignore if version changed while mutation was in-flight
            if (mutationVersionId !== versionIdRef.current) {
              return;
            }

            // THE QUEUE LOGIC: When request finishes, check if there was any pending save call
            if (pendingConfigRef.current !== null && !error) {
              const nextConfig = pendingConfigRef.current;
              pendingConfigRef.current = null;
              // Fire next save with fresh etag
              saveWithQueue(nextConfig);
            } else if (error) {
              // Clear pending on error to avoid stale/erroneous data being saved
              pendingConfigRef.current = null;
            }
          },
          onError: (error) => {
            logger.error("Failed to save agent version configuration", error);
            toastActions.failure({
              title: "Failed to save changes",
              description:
                error instanceof Error
                  ? error.message
                  : "An unknown error occurred",
            });
          },
        },
      );
    },

    // This dependency array is intentionally not exhaustive to avoid unnecessary re-renders
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMutation.mutate, onSave, agentId, versionId, name, description],
  );

  // The trigger function - checks if busy before firing
  const isPendingRef = useRef(false); // To avoid save being new every render
  isPendingRef.current = updateMutation.isPending;
  const save = useCallback(
    (configuration: AgentVersionConfigurationInput) => {
      if (disabled) return;

      // Skip save if configuration hasn't actually changed
      // This prevents redundant API calls when form subscription fires
      // multiple times with the same effective value
      // Prunning undefined values from the original configuration to avoid false positives.
      if (fastDeepEqual(prune(configuration), lastSavedConfigRef.current)) {
        return;
      }

      if (isPendingRef.current) {
        // BUSY: Queue it (overwrites previous - latest wins)
        pendingConfigRef.current = configuration;
      } else {
        // IDLE: Fire immediately
        saveWithQueue(configuration);
      }
    },
    [saveWithQueue, disabled],
  );

  // Debounced save
  const debouncedSave = useMemo(() => debounceAndSaveArgs(save, 500), [save]);

  // Pending state: debounce waiting OR mutation in-flight OR waiting room has data
  const hasPendingChanges =
    debouncedSave.pending() ||
    updateMutation.isPending ||
    pendingConfigRef.current !== null;

  return {
    save: debouncedSave,
    hasPendingChanges,
    isSaving: updateMutation.isPending,
  };
};
